// File: components/Chat/ChatMessageHandler.js
// Message rendering logic with OPTIMIZED logging, performance, and CACHED parsing

import React, { useState, memo, useMemo } from 'react';
import { parseReportTags, cleanMessageFromReportTags } from './reportTagParser';
import { MarkdownRenderer } from './ChatComponents';
import ChartRenderer from '../ChartRenderer';


// Enable/disable debug logging
const DEBUG_MODE = false;

const debugLog = (...args) => {
  if (DEBUG_MODE) {
    console.log(...args);
  }
};

// Cache outside components to persist across renders
const reportParseCache = new Map();

// Utility Functions for Message Processing
export const extractSuggestedPrompts = (messageText) => {
  if (!messageText || typeof messageText !== 'string') return [];

  const promptRegex = /<user_prompt>(.*?)<\/user_prompt>/gi;
  const prompts = [];
  let match;

  while ((match = promptRegex.exec(messageText)) !== null) {
    const promptText = match[1].trim();
    if (promptText) {
      prompts.push(promptText);
    }
  }

  return prompts;
};

export const renderContentWithCharts = (text) => {
  if (!text || !text.includes('<chart_data>')) {
    return <MarkdownRenderer content={text} />;
  }

  // Split content by chart tags
  const parts = text.split(/(<chart_data>[\s\S]*?<\/chart_data>)/g);

  return (
    <>
      {parts.map((part, index) => {
        const chartMatch = part.match(/^<chart_data>([\s\S]*?)<\/chart_data>$/);
        if (chartMatch) {
          try {
            return <ChartRenderer key={`chart-${index}`} chartJson={chartMatch[1]} />;
          } catch (e) {
            console.error('Failed to render chart:', e);
            return null;
          }
        }
        // Regular markdown content
        if (part.trim()) {
          return <MarkdownRenderer key={`text-${index}`} content={part} />;
        }
        return null;
      })}
    </>
  );
};


export const generateFallbackSuggestions = (messageText, messageIndex, totalMessages) => {
  return [];
};

export const filterStatusMessages = (messages) => {
  return messages.filter(msg => {
    if (!msg.text || typeof msg.text !== 'string') return false;
    const text = msg.text.trim();
    if (text === '' || text.length < 5) return false;
    
    const statusPhrases = [
      'thinking', 'processing', 'analyzing', 'generating',
      'extracting', 'finalizing', 'sending'
    ];
    
    const messageText = text.toLowerCase();
    const isStatusMessage = statusPhrases.some(phrase =>
      messageText.includes(phrase) && text.length < 50
    );
    
    return !isStatusMessage;
  });
};

// OPTIMIZED Report Links Component
export const ReportLinks = memo(({ reportData, onOpenReport, sonnetSendMessage }) => {
  const [refreshingUrls, setRefreshingUrls] = useState(new Set());
  
  // CHANGED: Only show full reports, ignore sections
  const hasReports = (reportData?.fullReports?.length > 0);
  
  if (!hasReports) {
    return null;
  }

  const handleReportClick = async (report) => {
    try {
      await onOpenReport(report);
    } catch (error) {
      console.error('❌ Error opening report:', error);
    }
  };

  return (
    <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
      <div className="space-y-3">
        
        {/* Full Report Links only */}
        {reportData.fullReports && reportData.fullReports.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Research Report
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                Ready to View
              </div>
            </div>
            
            {reportData.fullReports.map((report, idx) => {
              const refreshing = refreshingUrls.has(report.id);
              
              return (
                <div key={report.id || idx} className="flex items-center space-x-3">
                  <button
                    onClick={() => handleReportClick(report)}
                    disabled={refreshing}
                    className={`flex-1 inline-flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 group shadow-sm hover:shadow-md ${
                      refreshing
                        ? 'text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed'
                        : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/30'
                    }`}
                  >
                    {refreshing ? (
                      <svg className="w-5 h-5 mr-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    
                    <div className="flex-1 text-left">
                      <div className="font-semibold">{report.displayName || 'Complete Report'}</div>
                      <div className="text-xs opacity-75 mt-1">
                        {refreshing ? 'Loading...' : 'Click to view report'}
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* REMOVED: Section links no longer shown */}
      </div>
    </div>
  );
});

// Message Suggestions Component
export const MessageSuggestions = memo(({ suggestions, onSuggestionClick, wsConnected, isTyping }) => {
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-xl border border-blue-100 dark:border-blue-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
            Continue with:
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => onSuggestionClick(suggestion)}
            disabled={!wsConnected || isTyping}
            className="group relative inline-flex items-center justify-start px-4 py-3 text-sm font-medium rounded-lg border transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-900/30 dark:hover:text-blue-200"
          >
            <svg className="w-4 h-4 mr-3 flex-shrink-0 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="font-medium text-left flex-1">{suggestion}</span>
          </button>
        ))}
      </div>
    </div>
  );
});

// Empty State Component
export const EmptyState = memo(({ starterQuestions, refreshStarterQuestions, onSuggestionClick, wsConnected }) => (
  <div className="flex flex-col items-center justify-center h-full py-16">
    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 rounded-full flex items-center justify-center mb-6">
      <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    </div>
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
      Welcome to DataWeb AI
    </h2>
    <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-md text-center">
      Ask me anything about business research, market analysis, or industry insights.
    </p>

    <div className="hidden sm:block max-w-2xl w-full">
      <div className="flex items-center justify-between w-full mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Start with these questions:
        </h3>
        <button
          onClick={refreshStarterQuestions}
          disabled={!wsConnected}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/30 transition-all duration-200"
          title="Get 4 new random questions"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          New Questions
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
        {starterQuestions.map((questionObj, index) => (
          <button
            key={questionObj.id}
            onClick={() => onSuggestionClick(questionObj.question)}
            disabled={!wsConnected}
            className="group p-4 text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-[1.02] shadow-sm hover:shadow-md"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-lg">{questionObj.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {questionObj.question}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {questionObj.description}
                </p>
                <div className="flex items-center mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                    {questionObj.category}
                  </span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
));

// Main Message Renderer with CACHED parsing
export const MessageRenderer = memo(({ 
  message, 
  index, 
  messages, 
  showSuggestions, 
  isTyping, 
  isLoading, 
  onSuggestionClick, 
  onOpenReport, 
  wsConnected,
  sonnetSendMessage 
}) => {
  const isAIMessage = !message.isUser;
  
  // Use useMemo to cache parsing results per message
  const { reportData, cleanedText, explicitPrompts } = useMemo(() => {
    if (!isAIMessage) {
      return {
        reportData: { sections: [], fullReports: [], metadata: [] },
        cleanedText: message.text,
        explicitPrompts: []
      };
    }

    const cacheKey = message.id || `${message.text?.substring(0, 50)}_${index}`;
    
    if (reportParseCache.has(cacheKey)) {
      return reportParseCache.get(cacheKey);
    }
    
    const originalText = message.text;

    // ─── TEMPORARY DEBUG LOGGING ───
    // Check if the raw message contains any report-related content
    if (originalText && originalText.length > 200) {
      console.log('🔍 RAW AI MESSAGE (last 500 chars):', originalText.slice(-500));
      console.log('🔍 Contains REPORT_SAVED_TAG:', originalText.includes('REPORT_SAVED_TAG'));
      console.log('🔍 Contains <report_save>:', originalText.includes('<report_save>'));
      console.log('🔍 Contains <full_report>:', originalText.includes('<full_report>'));
      console.log('🔍 Contains report:', originalText.toLowerCase().includes('report'));
      console.log('🔍 Contains s3Key:', originalText.includes('s3Key'));
      console.log('🔍 Contains app_reports:', originalText.includes('app_reports'));
    }
    // ─── END DEBUG ───

    const parsedReportData = parseReportTags(originalText);
    const parsedCleanedText = cleanMessageFromReportTags(originalText);
    const parsedPrompts = extractSuggestedPrompts(originalText);
    
    const result = {
      reportData: parsedReportData,
      cleanedText: parsedCleanedText,
      explicitPrompts: parsedPrompts
    };
    
    reportParseCache.set(cacheKey, result);
    
    if (reportParseCache.size > 100) {
      const firstKey = reportParseCache.keys().next().value;
      reportParseCache.delete(firstKey);
    }
    
    return result;
  }, [message.id, message.text, message.isUser, isAIMessage, index]);
  
  const shouldShowSuggestions = explicitPrompts.length > 0;

  if (isAIMessage) {
    return (
      <div className="space-y-4">
        <div className="max-w-none">
          {renderContentWithCharts(cleanedText)}
        </div>

        {(reportData.fullReports?.length > 0) && (
            <ReportLinks 
                reportData={reportData} 
                onOpenReport={onOpenReport} 
                sonnetSendMessage={sonnetSendMessage}
            />
        )}

        {shouldShowSuggestions && (
          <MessageSuggestions 
            suggestions={explicitPrompts}
            onSuggestionClick={onSuggestionClick}
            wsConnected={wsConnected}
            isTyping={isTyping}
          />
        )}
      </div>
    );
  }

  return (
    <div className="text-white font-medium">{message.text}</div>
  );
});